import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  X,
  GraduationCap,
  Settings,
  Target
} from 'lucide-react';
import { ProfileStrengthData, ProfileField } from '@/types/profile';
import { getProfileCategories } from '@/config/profile-fields';

interface ProfileCompletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleteProfile: () => void;
  onNavigateToField?: (fieldKey: string, categoryKey: string) => void;
  strengthData: ProfileStrengthData;
  userType: 'student' | 'teacher';
}

const getCategoryIcon = (categoryKey: string) => {
  switch (categoryKey) {
    case 'basic_info':
      return User;
    case 'academic_info':
      return GraduationCap;
    case 'preferences':
      return Settings;
    case 'goals':
      return Target;
    default:
      return User;
  }
};

const getCategoryTitle = (categoryKey: string) => {
  switch (categoryKey) {
    case 'basic_info':
      return 'Basic Information';
    case 'academic_info':
      return 'Academic Information';
    case 'preferences':
      return 'Learning Preferences';
    case 'goals':
      return 'Goals & Objectives';
    default:
      return categoryKey.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

const getFieldDisplayName = (field: ProfileField) => {
  // Convert field keys to readable names
  const keyMap: Record<string, string> = {
    'first_name': 'First Name',
    'last_name': 'Last Name',
    'phone': 'Phone Number',
    'date_of_birth': 'Date of Birth',
    'student_details.class_grade': 'Class/Grade',
    'student_details.school_name': 'School Name',
    'student_details.academic_interests': 'Academic Interests',
    'student_details.learning_goals': 'Learning Goals',
    'student_details.learning_style': 'Learning Style',
    'student_details.preferred_subjects': 'Preferred Subjects',
    'student_details.study_schedule': 'Study Schedule',
  };
  
  return keyMap[field.key] || field.label || field.key;
};

// Color coding for progress bars
const getProgressColorClass = (strength: number) => {
  if (strength < 30) return "bg-destructive"
  if (strength < 70) return "bg-orange-500"
  return "bg-green-500"
};

// Text color for percentage display
const getTextColor = (strength: number) => {
  if (strength < 30) return "text-destructive"
  if (strength < 70) return "text-orange-600 dark:text-orange-400"
  return "text-green-600 dark:text-green-400"
};

export function ProfileCompletionDialog({
  isOpen,
  onClose,
  onCompleteProfile,
  onNavigateToField,
  strengthData,
  userType
}: ProfileCompletionDialogProps) {
  const completionPercentage = Math.floor(strengthData.totalStrength);
  const categoriesArray = Object.entries(strengthData.categories);
  
  // Get top 3 pending fields by weight
  const topPendingFields = strengthData.pendingFields
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 3);

  const handleDismiss = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDismiss}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-lg font-semibold">
            Complete Your Profile
          </DialogTitle>
          <DialogDescription className="text-sm">
            Your profile is <strong>{completionPercentage}% complete</strong>. 
            Complete it for personalized recommendations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Overall Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Overall Progress</span>
              <span className={`text-sm font-medium ${getTextColor(completionPercentage)}`}>
                {completionPercentage}%
              </span>
            </div>
            <Progress 
              value={completionPercentage} 
              className="h-2" 
              indicatorClassName={getProgressColorClass(completionPercentage)}
            />
          </div>

          {/* Compact Category Status */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm">Missing Information</h3>
            <div className="grid grid-cols-2 gap-2">
              {categoriesArray
                .filter(([_, stats]) => stats.strength < 100)
                .map(([categoryKey, stats]) => {
                  const Icon = getCategoryIcon(categoryKey);
                  const strength = Math.round(stats.strength);
                  
                  return (
                    <div key={categoryKey} className="flex items-center space-x-2 p-2 rounded-md border bg-muted/30">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {getCategoryTitle(categoryKey)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {stats.total - stats.completed} missing
                        </p>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Top Priority Field */}
          {topPendingFields.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-sm">Quick Win</h3>
              <div 
                className={`flex items-center space-x-2 p-2 rounded-md bg-primary/5 border border-primary/20 ${
                  onNavigateToField ? 'cursor-pointer hover:bg-primary/10 transition-colors' : ''
                }`}
                onClick={() => {
                  if (onNavigateToField) {
                    const field = topPendingFields[0];
                    onNavigateToField(field.key, field.category);
                    onClose();
                  }
                }}
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-medium text-primary">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{getFieldDisplayName(topPendingFields[0])}</p>
                  {onNavigateToField && (
                    <p className="text-xs text-muted-foreground">Click to edit this field</p>
                  )}
                </div>
                <Badge variant="outline" className="text-xs">
                  +{topPendingFields[0].weight}
                </Badge>
                {onNavigateToField && (
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                )}
              </div>
            </div>
          )}

          {/* Compact Benefits */}
          <div className="p-3 rounded-md bg-blue-50/50 border border-blue-200">
            <p className="text-xs text-blue-800">
              <strong>Complete your profile</strong> for personalized recommendations and better learning experience.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button onClick={onCompleteProfile} className="flex-1" size="sm">
            Complete Now
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          <Button variant="outline" onClick={handleDismiss} size="sm">
            Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
